import { useEffect, useState, Fragment } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  Menu,
  Phone,
  Mail,
  ArrowRight,
  Clock,
  ChevronDown,
  ClipboardCheck,
  ShieldCheck,
  Car,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { SocialBar } from "@/components/SocialBar";
import { LanguageToggle } from "@/components/LanguageToggle";
import { navLinks, hotlines, openingHours, site } from "@/lib/siteConfig";
import { serviceLinks } from "@/lib/serviceContent";
import { commonContent } from "@/cms/cmsModel";
import { useCmsDocument } from "@/cms/CmsContentProvider";

const iconMap = { ClipboardCheck, ShieldCheck, Car, Sparkles };

export const Navbar = () => {
  const copy = commonContent.navbar;
  const { locale } = useCmsDocument();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Premium micro-interaction: the header becomes slightly more compact with a
  // stronger blur + shadow once the visitor scrolls past the top.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `relative px-1 py-2 text-[0.95rem] font-semibold tracking-wide transition-colors duration-200 az-link-underline ${
      isActive ? "text-[#7A1516]" : "text-[#26303c] hover:text-[#7A1516]"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-[#D5DEE8] bg-[#F1F4F8]/90 shadow-[0_8px_30px_rgba(25, 61, 109,0.10)] backdrop-blur-md"
          : "border-[#D5DEE8]/70 bg-[#F1F4F8]/95 backdrop-blur supports-[backdrop-filter]:bg-[#F1F4F8]/80"
      }`}
    >
      <nav
        className={`az-container flex items-center justify-between gap-4 transition-all duration-300 ${
          scrolled ? "py-1.5" : "py-2"
        }`}
      >
<Logo
  variant="light"
  imgClassName={`w-auto select-none object-contain transition-all duration-300 ${
    scrolled ? "h-3 sm:h-6" : "h-5 sm:h-8"
  }`}
/>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => {
            if (l.to === "/partnernetzwerk") {
              return (
                <div key={l.to} className="relative group" data-testid="nav-partnernetzwerk-group">
                  <div className="flex items-center gap-1">
                    <NavLink to={l.to} className={linkClass} data-testid={l.testid}>
                      {l.label}
                    </NavLink>
                    <ChevronDown
                      size={15}
                      aria-hidden="true"
                      className="text-[#6e7683] transition-transform duration-200 group-hover:rotate-180 group-hover:text-[#7A1516]"
                    />
                  </div>
                  {/* Dropdown menu */}
                  <div
                    className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 translate-y-1 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100"
                    data-testid="services-dropdown"
                  >
                    <div className="overflow-hidden rounded-2xl border border-[#D5DEE8] bg-[#F1F4F8] p-2 shadow-[0_18px_40px_rgba(25, 61, 109,0.16)]">
                      {serviceLinks.map((sl) => {
                        const Icon = iconMap[sl.icon] || Sparkles;
                        return (
                          <Link
                            key={sl.to}
                            to={sl.to}
                            data-testid={`services-dropdown-${sl.id}`}
                            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#26303c] transition-colors hover:bg-[#7A1516]/[0.08] hover:text-[#7A1516]"
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#7A1516]/[0.08] text-[#7A1516]">
                              <Icon size={16} />
                            </span>
                            {sl.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={l.testid} end={l.to === "/"}>
                {l.label}
              </NavLink>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <a href={`tel:${site.primaryPhoneTel}`} data-testid="navbar-call-link">
            <Button variant="outline" className="rounded-xl border-[#6e7683]/40 text-[#193D6D] hover:bg-[#6e7683]/10">
              <Phone size={16} className="mr-2" /> {copy.phoneButton}
            </Button>
          </a>
          <Link to="/kontakt" data-testid="navbar-contact-cta">
            <Button className="rounded-xl bg-[#7A1516] text-[#F1F4F8] hover:bg-[#941d1e]">
              {copy.contactButton} <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>

        {/* Mobile */}
        <div className="flex lg:hidden items-center gap-2">
          <LanguageToggle compact />
          <a href={`tel:${site.primaryPhoneTel}`} aria-label={locale === "ar" ? "اتصال" : "Anrufen"} data-testid="navbar-call-mobile">
            <Button
              size="icon"
              variant="outline"
              className="rounded-xl border-[#6e7683]/40 text-[var(--az-icon-default)] hover:text-[var(--az-icon-hover)] focus-visible:text-[var(--az-icon-hover)] active:text-[var(--az-icon-hover)]"
            >
              <Phone size={18} />
            </Button>
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button size="icon" className="rounded-xl bg-[#7A1516] text-white hover:bg-[#941d1e]" data-testid="nav-mobile-menu-button">
                <Menu size={20} />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[88%] max-w-sm bg-[#F1F4F8] p-0"
              style={{
                height: "100dvh",
                maxHeight: "100dvh",
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
                paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
              }}
            >
              <SheetTitle className="sr-only">{locale === "ar" ? "قائمة التنقل" : "Navigationsmenü"}</SheetTitle>
              <SheetDescription className="sr-only">
                {locale === "ar" ? "التنقل الرئيسي وروابط الصفحات وطرق التواصل" : "Hauptnavigation mit Links zu allen Seiten und Kontaktmöglichkeiten"}
              </SheetDescription>
              <div className="flex min-h-full flex-col">
                <div className="border-b border-[#D5DEE8] p-5">
                  <Logo variant="light" />
                </div>
                <div className="border-b border-[#D5DEE8] px-5 py-4">
                  <span className="mb-2.5 block text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-[#6e7683]">
                    {copy.socialTitle}
                  </span>
                  <SocialBar size="lg" layout="grid3" testidPrefix="mobile-social" className="w-full" />
                </div>
                <div className="flex flex-col gap-1 p-5">
                  {navLinks.map((l) => (
                    <Fragment key={l.to}>
                      <SheetClose asChild>
                        <NavLink
                          to={l.to}
                          end={l.to === "/"}
                          data-testid={`mobile-${l.testid}`}
                          className={({ isActive }) =>
                            `rounded-lg px-3 py-3 text-lg font-semibold transition-colors ${
                              isActive ? "bg-[#7A1516]/10 text-[#7A1516]" : "text-[#26303c] hover:bg-[#193D6D]/5"
                            }`
                          }
                        >
                          {l.label}
                        </NavLink>
                      </SheetClose>

                      {l.to === "/partnernetzwerk" && (
                        <div
                          className="mb-1 ml-3 flex flex-col gap-0.5 border-l border-[#D5DEE8] pl-3"
                          data-testid="mobile-services-sublinks"
                        >
                          {serviceLinks.map((sl) => {
                            const Icon = iconMap[sl.icon] || Sparkles;
                            return (
                              <SheetClose asChild key={sl.to}>
                                <NavLink
                                  to={sl.to}
                                  data-testid={`mobile-services-${sl.id}`}
                                  className={({ isActive }) =>
                                    `flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-base font-medium transition-colors ${
                                      isActive
                                        ? "bg-[#7A1516]/10 text-[#7A1516]"
                                        : "text-[#586576] hover:bg-[#193D6D]/5 hover:text-[#7A1516]"
                                    }`
                                  }
                                >
                                  <Icon size={16} className="text-[#7A1516]" /> {sl.label}
                                </NavLink>
                              </SheetClose>
                            );
                          })}
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>

                <div className="mt-auto space-y-4 border-t border-[#D5DEE8] p-5">
                  <div className="space-y-2.5">
                    {hotlines.map((h) => (
                      <a key={h.id} href={`tel:${h.tel}`} className="group flex items-center gap-3 text-sm">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[var(--az-icon-default)] transition-colors group-hover:text-[var(--az-icon-hover)] group-focus-visible:text-[var(--az-icon-hover)] group-active:text-[var(--az-icon-hover)]">
                          <Phone size={14} />
                        </span>
                        <span>
                          <span className="block text-[0.7rem] uppercase tracking-wide text-[#6e7683]">{h.label}</span>
                          <span className="font-semibold text-[#193D6D]">{h.number}</span>
                        </span>
                      </a>
                    ))}
                  </div>
                  <div className="flex items-start gap-3 text-sm text-[#586576]">
                    <Clock size={16} className="mt-0.5 text-[#7A1516]" />
                    <span>
                      {openingHours.map((o) => (
                        <span key={o.days} className="block">
                          {o.days}: {o.time}
                        </span>
                      ))}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <SheetClose asChild>
                      <Link to="/kontakt">
                        <Button className="w-full rounded-xl bg-[#7A1516] text-[#F1F4F8] hover:bg-[#941d1e]">
                          {copy.contactButton}
                        </Button>
                      </Link>
                    </SheetClose>
                    <a href={`mailto:${site.email}`}>
                      <Button variant="outline" className="w-full rounded-xl border-[#7A1516]/40 text-[#7A1516]">
                        <Mail size={16} className="mr-2" /> {copy.emailButton}
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Mobile quick-contact strip — six icons, always visible (no hamburger needed) */}
      <div
        className="lg:hidden border-t border-[#D5DEE8] bg-[#F1F4F8]/95 px-3 py-2"
        data-testid="mobile-social-strip"
      >
        <SocialBar layout="grid6" testidPrefix="mobile-strip-social" className="mx-auto w-full max-w-xs" />
      </div>
    </header>
  );
};
